import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Building2 } from 'lucide-react';

import { Alert, Badge, Card, CardBody, CardHeader, PageHeader, Spinner, Table } from '@platform/ui';

import { HealthSummary, TenantStatusBadge } from '../components/status-badges';
import { PlatformHealthCard } from '../features/logs/platform-health-card';
import { tenantOptionsKey } from '../features/tenants/use-tenant';
import { usePlatformClient } from '../lib/api/platform-client-context';
import { APP_BUILD, useApiVersion } from '../version';

export const Route = createFileRoute('/')({
  component: Overview,
});

/**
 * The console's front page (MASTER_PLAN M12.7).
 *
 * It answers one question — is anything wrong anywhere — and then gets out of the way.
 * The real health board is `GET /platform/health/tenants` (M12.4); until then it is
 * derived from the same tenant list the next screen shows.
 */
function Overview() {
  const client = usePlatformClient();
  const api = useApiVersion();

  /*
   * The whole list in one page: the front page counts clinics, and a count that stopped
   * at the first cursor would be wrong rather than incomplete. `limit` is the API's
   * maximum (API.md §1.7); past a hundred clinics this becomes a server-side aggregate.
   */
  const tenants = useQuery({
    queryKey: tenantOptionsKey(),
    queryFn: async () => (await client.listTenants({ limit: 100 })).data,
  });

  /*
   * The platform's own health (M12.4), from the same seam `/logs` reads, so the front
   * page and the screen it links to cannot disagree about how many jobs are stuck.
   */
  const health = useQuery({
    queryKey: ['platform-health'],
    queryFn: () => client.getPlatformHealth(),
  });

  const rows = tenants.data ?? [];
  const attention = rows.filter(
    (tenant) =>
      tenant.status !== 'active' || tenant.health.failed > 0 || tenant.health.degraded > 0,
  );

  return (
    <>
      <PageHeader
        title="Overview"
        description="Object Labs operates HeyAllergy from this console."
      />

      {tenants.isPending ? (
        <Card className="p-6">
          <Spinner label="Loading tenants" />
        </Card>
      ) : tenants.isError ? (
        <Alert tone="error">The tenant list could not be loaded.</Alert>
      ) : (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <Stat label="Tenants" value={String(rows.length)} />
            <Stat
              label="Active"
              value={String(rows.filter((tenant) => tenant.status === 'active').length)}
            />
            <Stat label="Needing attention" value={String(attention.length)} />
          </div>

          <PlatformHealthCard
            health={health.data ?? null}
            pending={health.isPending}
            logsLink={
              <Link
                to="/logs"
                search={{ tab: 'queues' as const }}
                className="text-primary-accessible hover:underline"
              >
                Open logs
              </Link>
            }
          />

          <Card className="mt-6">
            <CardHeader
              title="Needing attention"
              subtitle="Suspended or archived clinics, and any integration that is failing or degraded."
              actions={attention.length === 0 ? <Badge tone="success">All clear</Badge> : undefined}
            />
            <Table
              className="rounded-none border-0 shadow-none"
              caption="Tenants needing attention"
              rows={attention}
              getRowKey={(tenant) => tenant.id}
              empty={
                <div className="px-6 py-10 text-center text-base text-neutral-500">
                  Every clinic is active and every integration is healthy.
                </div>
              }
              columns={[
                {
                  key: 'name',
                  header: 'Clinic',
                  render: (tenant) => (
                    <Link
                      to="/tenants/$tenantId"
                      params={{ tenantId: tenant.id }}
                      className="font-medium text-primary-accessible hover:underline"
                    >
                      {tenant.name}
                    </Link>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (tenant) => <TenantStatusBadge status={tenant.status} />,
                },
                {
                  key: 'health',
                  header: 'Integrations',
                  render: (tenant) => <HealthSummary summary={tenant.health} />,
                },
              ]}
            />
          </Card>
        </>
      )}

      <Card className="mt-6">
        <CardHeader title="Build" subtitle="This bundle and the API it is talking to." />
        <CardBody>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-base text-neutral-500">
            <dt>App build</dt>
            <dd className="text-ink">
              {APP_BUILD.version} ({APP_BUILD.commit})
            </dd>
            <dt>API build</dt>
            <dd className="text-ink">
              {api === undefined ? 'not reachable' : `${api.version} (${api.commit})`}
            </dd>
          </dl>
        </CardBody>
      </Card>

      <p className="mt-6 flex items-center gap-2 text-sm text-neutral-500">
        <Building2 size={16} strokeWidth={2} aria-hidden="true" />
        {/* M13.7: in a text block, so underlined at rest (WCAG 1.4.1). */}
        <Link to="/tenants" className="text-primary-accessible underline underline-offset-2">
          Go to tenants
        </Link>
      </p>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardBody>
        <p className="text-2xs font-medium tracking-badge text-neutral-400-text uppercase">
          {label}
        </p>
        <p className="mt-1 text-3xl font-bold text-ink tabular-nums">{value}</p>
      </CardBody>
    </Card>
  );
}
